import clsx from 'clsx';
import CarouselButtons from 'components/carousel2025/CarouselButtons';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import ImageWithFallback from 'components/ImageWithFallback';
import useEmblaCarousel from 'embla-carousel-react';
import Link from 'next/link';
import { CSSProperties } from 'react';
import { PrizeCarouselItem } from 'types/PrizeCarousel';

import styles from './SharedPrizeCarousel.module.css';

interface SharedPrizeCarouselProps {
  items: PrizeCarouselItem[];
  mobileCardWidth?: string;
  desktopCardWidth?: string;
  desktopInlinePadding?: string;
  slidePadding?: string;
  textPosition?: 'default' | 'overlayBottom';
  overlayVariant?: 'gradient' | 'solid';
  titleClassName?: string;
  subheadingClassName?: string;
  showButtonsOnMobile?: boolean;
  showButtonsOnDesktop?: boolean;
  linkBuilder?: (item: PrizeCarouselItem) => string | null | undefined;
  variant?: 'HomePage2026V4';
}

export const SharedPrizeCarousel = ({
  items,
  mobileCardWidth = '90%',
  desktopCardWidth = '30%',
  desktopInlinePadding = '40px',
  slidePadding,
  textPosition = 'default',
  overlayVariant = 'gradient',
  titleClassName,
  subheadingClassName,
  showButtonsOnMobile = true,
  showButtonsOnDesktop = true,
  linkBuilder,
  variant,
}: SharedPrizeCarouselProps) => {
  const emblaCarouselInfo = useEmblaCarousel({
    loop: !variant,
    align: 'start',
    duration: 10,
    inViewThreshold: 0.1,
  });

  if (items.length === 0) {
    return null;
  }

  const carouselStyle = {
    '--prize-carousel-mobile-item-width': mobileCardWidth,
    '--prize-carousel-desktop-item-width': desktopCardWidth,
    '--prize-carousel-inline-padding': desktopInlinePadding,
    '--prize-carousel-button-display-mobile': showButtonsOnMobile
      ? 'block'
      : 'none',
    '--prize-carousel-button-display-desktop': showButtonsOnDesktop
      ? 'block'
      : 'none',
    ...(slidePadding ? { '--prize-carousel-slide-padding': slidePadding } : {}),
  } as CSSProperties;

  return (
    <div className={styles.carouselContainer} style={carouselStyle}>
      <EmblaCarousel
        className={styles.prizeCarousel}
        emblaCarouselInfo={emblaCarouselInfo}
        pips2026={variant === 'HomePage2026V4' && 'lighter'}
        items={items.map((item, index) => {
          const textContentClass = [
            styles.carouselTextContent,
            textPosition === 'overlayBottom' ? styles.overlayBottom : '',
          ]
            .filter(Boolean)
            .join(' ');

          const body = (
            <div className={styles.carouselLinkContent}>
              <div className={styles.carouselImageWrapper}>
                <ImageWithFallback
                  layout="fill"
                  objectFit="cover"
                  src={item.imageSrc}
                  width={600}
                  height={600}
                  alt={item.imageAlt ?? item.title}
                  className={styles.carouselImage}
                />
                <div
                  className={[
                    styles.carouselOverlay,
                    overlayVariant === 'solid'
                      ? styles.overlaySolid
                      : styles.overlayGradient,
                  ].join(' ')}
                />
              </div>
              <div className={textContentClass}>
                <h3
                  className={clsx(
                    !variant && 'aboutUsTypographyHeaderMedium',
                    variant === 'HomePage2026V4' && 'daymadeTypographyMMedium',
                    'textAlignLeft',
                    titleClassName,
                  )}
                >
                  {item.title}
                </h3>
                <p
                  className={clsx(
                    variant === 'HomePage2026V4' && 'daymadeTypographySRegular',
                    variant === 'HomePage2026V4'
                      ? styles.itemSubheading
                      : 'noMargin',
                    subheadingClassName,
                  )}
                >
                  {item.subheading}
                </p>
              </div>
            </div>
          );

          const href = linkBuilder?.(item);

          return (
            <div
              key={item.id ?? item.slug ?? index}
              className={styles.carouselSlide}
            >
              {href ? <Link href={href}>{body}</Link> : body}
            </div>
          );
        })}
      />
      <CarouselButtons
        changeSelectedIndex={(x) =>
          x < 0
            ? emblaCarouselInfo[1]?.scrollPrev()
            : emblaCarouselInfo[1]?.scrollNext()
        }
      />
    </div>
  );
};
